import { Controller } from "@hotwired/stimulus"

// 사용되는 아이콘들을 미리 import (Vite 빌드 시점에 번들링됨)
// exports 설정에 따라 @daangn/vue2-monochrome-icon/IconName 형식으로 import
import IconXmarkLine from '@daangn/vue2-monochrome-icon/IconXmarkLine'
import IconChevronLeftLine from '@daangn/vue2-monochrome-icon/IconChevronLeftLine'
import IconChevronRightLine from '@daangn/vue2-monochrome-icon/IconChevronRightLine'
import IconExclamationmarkCircleLine from '@daangn/vue2-monochrome-icon/IconExclamationmarkCircleLine'
import IconDocumentMagnifyingglassFill from '@daangn/vue2-monochrome-icon/IconDocumentMagnifyingglassFill'
import IconDocumentCheckmarkFill from '@daangn/vue2-monochrome-icon/IconDocumentCheckmarkFill'
import IconLockFill from '@daangn/vue2-monochrome-icon/IconLockFill'
import IconMagnifyingglassFill from '@daangn/vue2-monochrome-icon/IconMagnifyingglassFill'
import IconClockFill from '@daangn/vue2-monochrome-icon/IconClockFill'
import IconWonCircleArrowRightLine from '@daangn/vue2-monochrome-icon/IconWonCircleArrowRightLine'
import IconPersonFill from '@daangn/vue2-monochrome-icon/IconPersonFill'
import IconSlashCircleLine from '@daangn/vue2-monochrome-icon/IconSlashCircleLine'
import IconPersonShieldFill from '@daangn/vue2-monochrome-icon/IconPersonShieldFill'
import IconCalendarFill from '@daangn/vue2-monochrome-icon/IconCalendarFill'
import IconDot3HorizontalChatbubbleLeftSlashFill from '@daangn/vue2-monochrome-icon/IconDot3HorizontalChatbubbleLeftSlashFill'
import IconArrowRightArrowLeftLine from '@daangn/vue2-monochrome-icon/IconArrowRightArrowLeftLine'

// 아이콘 이름(snake_case) -> 컴포넌트 매핑
const iconMap = {
  'xmark_line': IconXmarkLine,
  'chevron_left_line': IconChevronLeftLine,
  'chevron_right_line': IconChevronRightLine,
  'exclamationmark_circle_line': IconExclamationmarkCircleLine,
  'document_magnifyingglass_fill': IconDocumentMagnifyingglassFill,
  'document_checkmark_fill': IconDocumentCheckmarkFill,
  'lock_fill': IconLockFill,
  'magnifyingglass_fill': IconMagnifyingglassFill,
  'clock_fill': IconClockFill,
  'won_circle_arrow_right_line': IconWonCircleArrowRightLine,
  'person_fill': IconPersonFill,
  'slash_circle_line': IconSlashCircleLine,
  'person_shield_fill': IconPersonShieldFill,
  'calendar_fill': IconCalendarFill,
  'dot3_horizontal_chatbubble_left_slash_fill': IconDot3HorizontalChatbubbleLeftSlashFill,
  'arrow_right_arrow_left_line': IconArrowRightArrowLeftLine
}

/**
 * Seed Design System 아이콘을 렌더링하는 Stimulus 컨트롤러
 *
 * 사용법:
 * <span data-controller="seed-icon"
 *       data-seed-icon-name-value="chevron_left_line"
 *       data-seed-icon-size-value="24"></span>
 */
export default class extends Controller {
  static values = {
    name: String,
    size: { type: Number, default: 24 }
  }

  connect() {
    this.renderIcon()
  }

  renderIcon() {
    if (!this.nameValue) {
      console.warn('seed-icon: name value is required')
      return
    }

    const IconComponent = iconMap[this.nameValue]

    if (!IconComponent) {
      console.error(`seed-icon: Unknown icon "${this.nameValue}". Add it to iconMap in seed_icon_controller.js`)
      return
    }

    try {
      // Vue 컴포넌트에서 SVG 추출
      const size = this.sizeValue
      const svg = this.extractSVGFromComponent(IconComponent, size)

      if (svg) {
        this.element.innerHTML = svg
        this.element.style.display = 'inline-flex'
        this.element.style.alignItems = 'center'
        this.element.style.justifyContent = 'center'
      }
    } catch (error) {
      console.error(`Failed to render icon: ${this.nameValue}`, error)
    }
  }

  extractSVGFromComponent(component, size) {
    // Vue 컴포넌트의 render 함수를 문자열로 변환하여 SVG 데이터 추출
    const renderFn = component.render || (component.options && component.options.render)

    if (!renderFn) {
      console.error('No render function found')
      return null
    }

    // render 함수를 실행하기 위한 간단한 mock context
    const mockContext = {
      size: size,
      _c: (tag, data, children) => ({ tag, data, children }),
      _self: {
        _c: function(tag, data, children) {
          return { tag, data, children }
        }
      }
    }

    try {
      const result = renderFn.call(mockContext)
      return this.renderVNode(result, size)
    } catch (error) {
      console.error('Failed to extract SVG:', error)
      return null
    }
  }

  renderVNode(vnode, size) {
    if (!vnode) return ''

    const { tag, data = {}, children = [] } = vnode
    const attrs = data.attrs || {}

    // SVG 태그 생성
    let html = `<${tag}`

    // 속성 추가
    for (const [key, value] of Object.entries(attrs)) {
      if (key === 'width' || key === 'height') {
        html += ` ${key}="${size}"`
      } else if (key === 'fill') {
        // fill을 currentColor로 오버라이드하여 CSS로 제어 가능하게
        html += ` ${key}="currentColor"`
      } else {
        html += ` ${key}="${value}"`
      }
    }

    // path 요소는 항상 fill="currentColor" 적용하여 CSS color 상속
    if (tag === 'path') {
      if (!attrs.fill) {
        html += ' fill="currentColor"'
      }
      // 이미 fill이 있어도 currentColor로 오버라이드됨 (위의 else if 분기에서 처리)
    }

    html += '>'

    // 자식 요소 렌더링
    if (children && children.length > 0) {
      children.forEach(child => {
        html += this.renderVNode(child, size)
      })
    }

    html += `</${tag}>`

    return html
  }
}
